import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../common/Modal';
import { useToast } from '../common/Toast';
import { useLanguage } from '../../features/language/useLanguage';
import { onboardingService } from '../../services/onboardingService';
import { useOnboardingStore } from '../../store/onboardingStore';

interface OnboardingCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const OnboardingCompletionModal: React.FC<OnboardingCompletionModalProps> = ({ isOpen, onClose }) => {
  const [phase, setPhase] = useState<'confirm' | 'done'>('confirm');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { t } = useLanguage();
  const setState = useOnboardingStore((s) => s.setState);

  const handleFinish = async () => {
    setBusy(true);
    try {
      const { state } = await onboardingService.complete();
      setState(state);
      setPhase('done');
    } catch {
      showToast(t('onboarding.completion.error', undefined, 'Could not finish the tour. Please try again.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={phase === 'confirm'
        ? t('onboarding.completion.confirmTitle', undefined, 'Finish your tour')
        : t('onboarding.completion.doneTitle', undefined, "You're all set")}
      maxWidth="md"
    >
      {phase === 'confirm' ? (
        <div className="space-y-4" data-test-id="onboarding-completion-confirm">
          <p className="text-sm text-gray-700">
            {t(
              'onboarding.completion.confirmBody',
              undefined,
              "We'll remove your practice purchase, roast batch, cupping entry and sale. The inventory you entered in step 1 stays exactly as you left it.",
            )}
          </p>
          <div className="flex justify-end gap-2">
            <button className="px-3 py-1.5 text-sm text-gray-500" onClick={onClose} disabled={busy}>{t('onboarding.completion.notYet', undefined, 'Not yet')}</button>
            <button
              data-test-id="onboarding-finish"
              className="rounded bg-amber-600 px-3 py-1.5 text-sm text-white disabled:opacity-50"
              onClick={handleFinish}
              disabled={busy}
            >
              {busy ? t('onboarding.completion.cleaningUp', undefined, 'Cleaning up…') : t('onboarding.completion.finishCleanUp', undefined, 'Finish & clean up')}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4" data-test-id="onboarding-completion-done">
          <p className="text-sm text-gray-700">{t('onboarding.completion.donePrompt', undefined, 'What do you need to do next?')}</p>
          <div className="grid gap-2">
            <button className="rounded border px-3 py-2 text-sm hover:bg-gray-50" onClick={() => go('/purchase-orders')}>{t('onboarding.completion.buyBeans', undefined, 'Buy beans')}</button>
            <button className="rounded border px-3 py-2 text-sm hover:bg-gray-50" onClick={() => go('/work-orders')}>{t('onboarding.completion.roast', undefined, 'Roast what I have')}</button>
            <button className="rounded border px-3 py-2 text-sm hover:bg-gray-50" onClick={() => go('/sales-orders')}>{t('onboarding.completion.sell', undefined, 'Sell finished stock')}</button>
          </div>
        </div>
      )}
    </Modal>
  );
};
